
import React, { useState } from 'react';
import { ShoppingCart, Trash2, Plus, Minus, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/lib/utils';

interface CartItem {
  id: string;
  description: string;
  quantity_in_base_units: number;
  unit_cost_snapshot: number;
  total_cost_snapshot: number;
  component_type: string;
  base_unit: string;
}

// Quantity Controls Component for inline editing
interface QuantityControlsProps {
  quantity: number;
  onUpdateQuantity: (quantity: number) => void;
  isUpdating: boolean;
}

const QuantityControls: React.FC<QuantityControlsProps> = ({
  quantity,
  onUpdateQuantity,
  isUpdating,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(quantity.toString());

  const handleStartEdit = () => {
    setEditValue(quantity.toString());
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const parsedValue = parseInt(editValue);
    const newQuantity = isNaN(parsedValue) || parsedValue < 1 ? 1 : parsedValue;
    onUpdateQuantity(newQuantity);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(quantity.toString());
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = () => {
    handleSaveEdit();
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-1">
        <Input
          type="number"
          step="1"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          className="w-16 h-8 text-center"
          disabled={isUpdating}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onUpdateQuantity(Math.max(1, quantity - 1))}
        disabled={isUpdating || quantity <= 1}
        className="h-9 w-9 p-0 min-h-[36px]"
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      
      <span 
        className="font-medium min-w-[2rem] text-center cursor-pointer hover:bg-muted active:bg-muted/80 rounded px-2 py-1 transition-colors select-none"
        onClick={handleStartEdit}
        title="Tap to edit quantity"
      >
        {quantity}
      </span>
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => onUpdateQuantity(quantity + 1)}
        disabled={isUpdating}
        className="h-9 w-9 p-0 min-h-[36px]"
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

interface ShoppingCartSidebarProps {
  cartItems: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onProceed: () => void;
  isUpdating: boolean;
  isRemoving: boolean;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** When true, renders without collapse controls (for use inside Drawer) */
  isOverlay?: boolean;
}

const ShoppingCartSidebar: React.FC<ShoppingCartSidebarProps> = ({
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onProceed,
  isUpdating,
  isRemoving,
  isCollapsed = false,
  onToggleCollapsed,
  isOverlay = false,
}) => {
  const totalAmount = cartItems.reduce((sum, item) => sum + item.total_cost_snapshot, 0);
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity_in_base_units, 0);

  const getComponentTypeBadge = (type: string) => {
    const variants: Record<string, string> = {
      internal_line_item: 'bg-blue-100 text-blue-800',
      supplier_line_item: 'bg-green-100 text-green-800',
      assembly: 'bg-purple-100 text-purple-800',
      custom: 'bg-orange-100 text-orange-800',
      system_line_item: 'bg-teal-100 text-teal-800',
    };
    
    const labels: Record<string, string> = {
      internal_line_item: 'Internal',
      supplier_line_item: 'Supplier',
      assembly: 'Assembly',
      custom: 'Custom',
      system_line_item: 'System',
    };

    return (
      <Badge variant="outline" className={`${variants[type] || ''} text-[10px] px-1.5 py-0`}>
        {labels[type] || type}
      </Badge>
    );
  };

  // Collapsed state (desktop only, not used in overlay)
  if (!isOverlay && isCollapsed) {
    return (
      <div className="w-12 h-full border-l flex flex-col items-center py-4 bg-background">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapsed}
          className="mb-4 h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-col items-center space-y-2">
          <ShoppingCart className="h-5 w-5 text-muted-foreground" />
          {cartItems.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {cartItems.length}
            </Badge>
          )}
        </div>
        {cartItems.length > 0 && (
          <div className="mt-4 text-xs text-center text-muted-foreground">
            R{totalAmount.toFixed(0)}
          </div>
        )}
      </div>
    );
  }

  const cartContent = (
    <>
      {cartItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-8">
          <ShoppingCart className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h3 className="font-medium mb-2">No items added</h3>
          <p className="text-sm text-muted-foreground">
            Browse and add items to your estimate
          </p>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="space-y-3">
            {cartItems.map((item) => (
              <div key={item.id} className="border border-border rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm line-clamp-2 mb-1">
                      {item.description}
                    </h4>
                    {getComponentTypeBadge(item.component_type)}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveItem(item.id)}
                    disabled={isRemoving}
                    className="ml-2 h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <QuantityControls
                    quantity={item.quantity_in_base_units}
                    onUpdateQuantity={(newQuantity) => onUpdateQuantity(item.id, newQuantity)}
                    isUpdating={isUpdating}
                  />

                  <div className="text-right">
                    <div className="font-medium text-sm">
                      {formatCurrency(item.total_cost_snapshot)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatCurrency(item.unit_cost_snapshot)} / {item.base_unit}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          {/* Cart Summary */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Items:</span>
              <span>{totalItems}</span>
            </div>
            
            <div className="flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(totalAmount)}</span>
            </div>

            <Button 
              onClick={onProceed}
              className="w-full min-h-[44px]"
              size="lg"
              disabled={cartItems.length === 0}
            >
              Continue to Final Details
            </Button>
          </div>
        </>
      )}
    </>
  );

  // Overlay mode (inside Drawer) - no Card wrapper
  if (isOverlay) {
    return <div className="p-1">{cartContent}</div>;
  }

  // Desktop inline mode
  return (
    <Card className="h-full flex flex-col bg-background border-none shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShoppingCart className="h-5 w-5" />
            <span>Estimate Items</span>
            {cartItems.length > 0 && (
              <Badge variant="secondary">{cartItems.length}</Badge>
            )}
          </div>
          {onToggleCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapsed}
              className="h-6 w-6 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col overflow-y-auto">
        {cartContent}
      </CardContent>
    </Card>
  );
};

export default ShoppingCartSidebar;
